/* eslint-disable no-nested-ternary */
import { Button, Col, notification, Row, Spin, Table, Tooltip, Typography } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { InfoCircleOutlined } from '@ant-design/icons'

import { BulkDataTypesEnum, RoutesFile } from '@types'
import { useFilters } from '@contexts'

import { useUploadBulkData } from './hooks/useUploadBulkData'
import { useGetBulkDataHistory } from './hooks/useGetBulkDataHistory'
import { StyledCenteredIcon, StyledModal, StyledTableRow } from '../StyledComponents'
import { useBulkDataHistoryPagination } from './hooks/useBulkDataPagination'
import { DownloadExcel } from '../DownloadExcel'

interface BulkDataUploadProps {
  isVisible: boolean
  onClose: () => void
  dataType: BulkDataTypesEnum
}

const BulkDataUploadModal = ({ isVisible, onClose, dataType }: BulkDataUploadProps) => {
  const { upload, isUploading } = useUploadBulkData(onClose)
  const { bulkDataFilters: filters, setBulkDataFilters: setFilters } = useFilters()
  const { t } = useTranslation()
  const tPrefix = 'globals.bulk_data_upload.'
  const { Text } = Typography
  const emptyInput = t(`${tPrefix}modal_msg`)
  const [fileChargeStatusMsg, setFileChargeStatusMsg] = useState(emptyInput)
  const [file, setFile] = useState<undefined | File>(undefined)
  const { bulkDataHistory, bulkDataHistoryTotal, isLoading } = useGetBulkDataHistory(filters)
  const pagination = useBulkDataHistoryPagination({ filters, setFilters }, bulkDataHistoryTotal)

  const handleImport = () => {
    const dataToImportInput = document.querySelector('#dataToImport') as HTMLInputElement
    dataToImportInput.click()
    dataToImportInput.onchange = e => {
      const { files } = e.target as HTMLInputElement
      if (files) {
        setFileChargeStatusMsg(files[0].name)
        setFile(files[0])
      } else if (files === null) {
        setFileChargeStatusMsg(emptyInput)
      }
    }
  }

  const formReset = () => {
    const fileInput = document.querySelector('#dataToImport') as HTMLInputElement
    fileInput.value = ''
    setFileChargeStatusMsg(emptyInput)
  }

  const columns = [
    {
      dataIndex: 'createAt',
      title: t(`${tPrefix}history_table.date`),
      render: (createAt: Date) => dayjs(createAt).format('lll'),
    },
    {
      dataIndex: 'fileName',
      title: t(`${tPrefix}history_table.file`),
      render: (fileName: string) => fileName,
    },
    {
      dataIndex: 'status',
      title: t(`${tPrefix}history_table.status.title`),
      render: (status: string) => {
        const statusToLower = status.toLowerCase()
        return (
          <>
            {statusToLower === 'processed with errors'
              ? 'Procesado con errores'
              : statusToLower === 'processed'
              ? 'Procesado correctamente'
              : statusToLower === 'not processed'
              ? 'No procesado'
              : statusToLower === 'in process'
              ? 'En proceso'
              : statusToLower === 'created'
              ? 'Esperando a ser procesado'
              : undefined}
          </>
        )
      },
    },
    {
      dataIndex: 'observations',
      title: t(`${tPrefix}history_table.observations`),
      render: (observations: string) =>
        observations === 'Invalid titles' ? 'Títulos no válidos' : observations,
    },
    {
      key: 'actions',
      title: (
        <StyledCenteredIcon>{t(`${tPrefix}history_table.actions.download`)}</StyledCenteredIcon>
      ),
      render: (route: RoutesFile) => {
        const statusToLower = route.status.toLowerCase()
        return statusToLower === 'not processed' ? null : <DownloadExcel id={route.id} />
      },
    },
  ]
  const inputReset = () => {
    formReset()
    setFile(undefined)
  }
  const handleUploadBulkData = () => {
    if (file) {
      upload(file)
      inputReset()
      notification.info({
        message: 'El archivo ha sido enviado para la importacion de rutas',
        key: 'submited',
      })
    } else {
      notification.error({ message: 'Debe seleccionar un archivo' })
    }
  }
  return (
    <StyledModal
      open={isVisible}
      title={
        <h1>
          {`${t(`${tPrefix}modal_title`)} - ${t(`globals.generics.${dataType.toLowerCase()}`)}`}
        </h1>
      }
      onCancel={() => {
        onClose()
        inputReset()
      }}
      className="bulk-data-upload"
      okText={t('globals.confirm')}
      centered
      width={850}
      footer={false}
    >
      <Col>
        <Row justify="space-between">
          <div>
            <Button type="link" onClick={handleImport}>
              {t(`${tPrefix}browse`)}
            </Button>
            <Text>
              {fileChargeStatusMsg}
              <Tooltip title={t('notifications.respect_column_layout_and_format')}>
                <InfoCircleOutlined className="info-upload-data-modal" />
              </Tooltip>
            </Text>
          </div>
          {file && (
            <Button type="primary" onClick={handleUploadBulkData}>
              {t('globals.upload')}
            </Button>
          )}
          {isUploading && (
            <Button type="link">
              <Spin spinning />
            </Button>
          )}
        </Row>
        <input type="file" accept=".csv, .xlsx" id="dataToImport" name="dataToImport" hidden />
      </Col>
      <StyledTableRow>
        <Table
          id="history-table"
          className="dataTable"
          columns={columns}
          rowKey="id"
          dataSource={bulkDataHistory}
          pagination={pagination}
          loading={isLoading}
          scroll={{ y: 300, x: true }}
        />
      </StyledTableRow>
    </StyledModal>
  )
}

export default BulkDataUploadModal
